import { css, cx } from '@linaria/core'
import { type ComponentProps, type CSSProperties, type FC, memo, useMemo } from 'react'
import { HoverTooltip } from '#components/HoverTooltip'

const IconClass = css`
  --icon-size: 16px;
  --mask-image: none;
  mask-image: var(--mask-image);
  display: inline-block;
  width: var(--icon-size);
  height: var(--icon-size);
  flex-shrink: 0;

  background-repeat: no-repeat;
  mask-repeat: no-repeat;
  mask-size: contain;
  background-color: currentColor;

  &.inverted {
    background: currentColor;
    border-radius: 50%;
    mask: var(--mask-image) center/calc(var(--icon-size) - 2px) no-repeat,
      // additional layer makes the mask-composite grab correctly as cutout
      linear-gradient(currentColor 0 0);
    mask-composite: exclude;
  }
`

const sizeToPixel = {
  default: '16px',
  xxs: '10px',
  xs: '12px',
  s: '20px',
  m: '24px',
  l: '32px',
  xl: '48px',
  xxl: '96px',
} as const

const SvgIcon: FC<
  {
    // language="File Reference"
    size?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | 'default' | number
    iconUrl?: string
    inverted?: boolean
  } & ComponentProps<'div'>
> = ({ ref, size = 's', iconUrl, style, className, inverted, ...props }) => {
  const combinedStyle = useMemo((): CSSProperties => {
    const iconSize = typeof size === 'number' ? `${size}px` : sizeToPixel[size]
    return {
      ...style,
      visibility: iconUrl ? undefined : 'hidden',
      '--mask-image': iconUrl ? `url(${iconUrl})` : 'none',
      '--icon-size': iconSize,
    } as CSSProperties
  }, [style, iconUrl, size])

  const Component = props.title ? HoverTooltip.div : 'div'
  const classNames = cx(IconClass, className, inverted && 'inverted')
  return <Component ref={ref} className={classNames} style={combinedStyle} {...props} />
}

/**
 * | `default` | `xxs` | `xs` | `s`  | `m` | `l`  | `xl` | `xxl` |
 * |:---------:|:-----:|:----:|:----:|:---:|:----:|:----:|:-----:|
 * | 16px      | 10px  | 12px | 20px | 24px| 32px | 48px | 96px  |
 */
export const Icon = memo(SvgIcon)
